import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem as DropDownMenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { PowerBIReport, PowerBIReportRef } from '.';
import { PowerBIReportHelpModal } from './PowerBIReportHelpModal';
import { useGetHelpContentQuery } from 'api/models/help';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';

import {
  AlertSeverity,
  MenuItem,
  PowerBiSlicerState,
  PowerBiSlicerStateRequest,
  setAlert,
  setCurrentMenuItem,
  setPbiSlicerFilterStates,
  useAddUserFavoriteMenuItemMutation,
  useAppDispatch,
  useAppSelector,
  useDeleteUserFavoriteMenuItemMutation,
  useGetMenuItemsQuery,
  useGetPowerBiQuery,
  useUpsertPowerBiSlicerStateMutation,
} from 'api';
import { PageContainer } from 'app';
import { PowerBiPageDropdown } from './PowerBiPageDropdown';
import { PowerBIReportRequestSupportModal } from './PowerBIReportRequestSupportModal';

export const PowerBIReportPage = () => {
  const dispatch = useAppDispatch();
  const { currentMenuItem } = useAppSelector(state => state.menuItem);
  const { pbiSlicerFilterStates } = useAppSelector(state => state.powerBi);
  const { mockUser } = useAppSelector(state => state.appUser);
  const mid = currentMenuItem?.id.toString() ?? '';
  const uid = mockUser?.id.toString() ?? '';
  const { data: helpContent } = useGetHelpContentQuery(
    { mid, uid },
    { skip: !mid || !uid }
  );
  const { data: menuItems, refetch } = useGetMenuItemsQuery(mockUser?.id, {
    skip: !mockUser,
  });
  const { data: powerBiData } = useGetPowerBiQuery(
    { mid, uid },
    { skip: !mid || !uid }
  );

  const [openHelpModal, setOpenHelpModal] = useState<boolean>(false);
  const [openRequestSupportModal, setOpenRequestSupportModal] =
    useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const prevOpen = useRef(open);
  const reportRef = useRef<PowerBIReportRef>(null);

  const [addFavorite] = useAddUserFavoriteMenuItemMutation();
  const [deleteFavorite] = useDeleteUserFavoriteMenuItemMutation();
  const [updateAction] = useUpsertPowerBiSlicerStateMutation();

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  function handleOpenHelpModal() {
    setOpenHelpModal(true);
  }

  function handleCloseHelpModal() {
    setOpenHelpModal(false);
  }

  function handleOpenRequestSupportModal() {
    setOpenRequestSupportModal(true);
  }

  function handleCloseRequestSupportModal() {
    setOpenRequestSupportModal(false);
  }

  function handleFavorite() {
    if (currentMenuItem) {
      if (!checkIsFavorited(currentMenuItem))
        addFavorite(currentMenuItem.id)
          .unwrap()
          .then(resp => {
            const updatedMenuItem = { ...currentMenuItem, isFavorited: true };
            dispatch(setCurrentMenuItem(updatedMenuItem));
            refetch();

            dispatch(
              setAlert({
                message: 'Favorite added.',
                severity: AlertSeverity.Success,
              })
            );
          })
          .catch(() => {
            dispatch(
              setAlert({
                message: 'Adding favorite failed.',
                severity: AlertSeverity.Error,
              })
            );
          });
      else if (checkIsFavorited(currentMenuItem))
        deleteFavorite(currentMenuItem.id)
          .unwrap()
          .then(resp => {
            const updatedMenuItem = { ...currentMenuItem, isFavorited: false };
            dispatch(setCurrentMenuItem(updatedMenuItem));
            refetch();

            dispatch(
              setAlert({
                message: 'Favorite removed.',
                severity: AlertSeverity.Success,
              })
            );
          })
          .catch(() => {
            dispatch(
              setAlert({
                message: 'Adding favorite failed.',
                severity: AlertSeverity.Error,
              })
            );
          });
    }
  }

  function checkIsFavorited(menuItem: MenuItem | undefined) {
    if (menuItems && menuItems?.favorites?.length <= 0) return false;
    if (menuItem) {
      return menuItems?.favorites.some((mi: MenuItem) => mi.id === menuItem.id);
    }
  }

  const handleSaveSlicers = async () => {
    if (powerBiData?.reportId && mockUser?.id && currentMenuItem?.id) {
      const powerBiSlicerStateRequest: PowerBiSlicerStateRequest = {
        powerBiReportId: powerBiData?.reportId,
        menuItemId: currentMenuItem?.id,
        userId: mockUser?.id,
        powerBiSlicerState:
          pbiSlicerFilterStates.length === 0
            ? ''
            : JSON.stringify(pbiSlicerFilterStates),
      };

      try {
        await updateAction(powerBiSlicerStateRequest)
          .unwrap()
          .then((response: PowerBiSlicerState) => {
            if (response.slicerState) {
              const powerBiSlicerStates = JSON.parse(response.slicerState);

              dispatch(setPbiSlicerFilterStates(powerBiSlicerStates));
            }
          });
        dispatch(
          setAlert({
            message: 'Filters saved.',
            severity: AlertSeverity.Success,
          })
        );
      } catch (e: any) {
        dispatch(
          setAlert({
            message: `Something went wrong: ${e.message}`,
            severity: AlertSeverity.Error,
          })
        );
      }
    }
  };

  const handleClearFilters = async (): Promise<void> => {
    try {
      await reportRef.current?.resetFilters();
      dispatch(
        setAlert({
          message: 'Filters cleared.',
          severity: AlertSeverity.Success,
        })
      );
    } catch (e: any) {
      dispatch(
        setAlert({
          message: `Something went wrong: ${e.message}`,
          severity: AlertSeverity.Error,
        })
      );
    }
  };

  return (
    <PageContainer powerBi>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" p={1}>
          {currentMenuItem?.text}
        </Typography>
        <Box display="flex" gap="1em">
          <Button
            ref={anchorRef}
            id="composition-button"
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            variant="contained"
            size="small"
          >
            Report Help
          </Button>
          <Button variant="outlined" onClick={handleSaveSlicers} size="small">
            Save Filters
          </Button>
          <Button variant="outlined" onClick={handleClearFilters} size="small">
            Clear Filters
          </Button>
          {checkIsFavorited(currentMenuItem) ? (
            <Tooltip
              title="Favorited"
              PopperProps={{ style: { marginTop: '0 !important' } }}
            >
              <IconButton onClick={handleFavorite}>
                <StarIcon sx={{ color: '#1b806d' }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title="Favorite"
              PopperProps={{ style: { marginTop: '0 !important' } }}
            >
              <IconButton onClick={handleFavorite}>
                <StarBorderIcon sx={{ color: '#1b806d' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ height: 'calc(100% - 105px)' }}>
        <PowerBIReport ref={reportRef} />
      </Box>
      <PowerBiPageDropdown />

      <PowerBIReportHelpModal
        open={openHelpModal}
        handleCloseModal={handleCloseHelpModal}
      />
      <PowerBIReportRequestSupportModal
        open={openRequestSupportModal}
        handleCloseModal={handleCloseRequestSupportModal}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  {helpContent && (
                    <DropDownMenuItem onClick={handleOpenHelpModal}>
                      View Documentation
                    </DropDownMenuItem>
                  )}

                  <DropDownMenuItem onClick={handleOpenRequestSupportModal}>
                    Request Support
                  </DropDownMenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </PageContainer>
  );
};
